import { createSlice } from '@reduxjs/toolkit';
import { find, isEqual } from 'lodash';
import moment from 'moment';
import { proc } from '@aexae/js_tools';

import socket from '../../utils/socket';

const { month, resourceId, contractId, checked } = proc.getQueryParams();
const currentMonth = moment().format('YYYYMM');

// Helper function to generate default filters with a parameter
// to indicate whether it's for a partner or not.
function createDefaultFilters(isPartner = false) {
  return {
    search: '',
    companies: [],
    sectors: [],
    contractTypes: [],
    jobs: [],
    languages: [],
    empowerments: [],
    formalities: [],
    degrees: [],
    equipments: [],
    external: isPartner, // TODO : rename with "isPartner"
    counters: checked === false ? '1' : 'all',
    dateAvailability: null,
    datetimeStart: null,
    datetimeEnd: null
  };
}

const initialState = {
  // Loading
  isLoading: false,
  isLoadingCounters: false,
  isLoadingContracts: false,
  isLoadingResource: false,
  isLoadingResourceDetails: false,
  isLoadingResourceEditForm: true, // TODO : voir pourquoi les autres états de loading sont initialisés à false
  // Errors
  error: false,
  // URLSearchParams
  contractId,
  month: month ? moment(month).format('YYYYMM') : currentMonth,
  pickerDate: month ? moment(month, 'YYYYMM').toDate() : moment().toDate(),
  resourceId,
  // Datas
  counters: undefined,
  contract: {},
  contracts: [],
  contractWithAvailabilityAccess: [],
  datesBounds: undefined,
  hasRights: true,
  keyToAvoid: null,
  resource: {},
  resources: [],
  resourceEditForm: {},
  // Api call props
  getResourcesProps: null,
  // Sort
  sortBy: ['fullname'],
  sortOrders: ['asc'],
  // Filters
  filterOptions: {
    companies: [],
    sectors: [],
    contractTypes: [],
    jobs: [],
    languages: [],
    empowerments: [],
    formalities: [],
    degrees: [],
    equipments: []
  },
  filterOptionsDatas: {
    companies: [],
    sectors: [],
    contractTypes: [],
    jobs: [],
    languages: [],
    empowerments: [],
    formalities: [],
    degrees: [],
    equipments: []
  },
  filters: createDefaultFilters()
};

const slice = createSlice({
  name: 'resources',
  initialState,
  reducers: {
    // LOADING
    startLoading: (state, action) => {
      const { loadingKey } = action.payload; // ex: 'isLoading', 'isLoadingCounters', etc.
      state[loadingKey] = true;
    },
    stopLoading: (state, action) => {
      const { loadingKey } = action.payload; // ex: 'isLoading', 'isLoadingCounters', etc.
      state[loadingKey] = false;
    },

    // RESET
    resetResource(state) {
      state.contract = {};
      state.contracts = [];
      state.degrees = [];
      state.empowerments = [];
      state.equipments = [];
      state.formalities = [];
      state.jobs = [];
      state.languages = [];
      state.resource = {};
      state.resourceEditForm = {};
      state.resources = [];
    },

    setRemoveDemand(state, action) {
      state.resource = { ...state.resource, hasRemoveDemand: action.payload };
    },

    // ERROR
    hasError(state, action) {
      state.error = action.payload;
      console.error(state.error);
      state.isLoading = false;
      state.isLoadingCounters = false;
      state.isLoadingResource = false;
      state.isLoadingResourceDetails = false;
      state.isLoadingResourceEditForm = false;
    },

    getCounters(state, action) {
      state.counters = action.payload.counters;
      state.isLoadingCounters = false;
    },

    getContractTypes(state, action) {
      state.filterOptions.contractTypes = action.payload.contractTypes;
      state.filterOptionsDatas.contractTypes = action.payload.contractTypes;
      // on retire les données superflues => on ressort les ids des données reçues en requête
      const dataResult = [...action.payload.contractTypes].map((el) => el.id);
      // on met à jour
      state.filters.contractTypes = state.filters.contractTypes.filter((data) => dataResult.includes(data));
    },

    getResource(state, action) {
      state.hasRights = action.payload.hasRights;
      state.resource = action.payload.resource;
    },

    getResourceContracts(state, action) {
      state.datesBounds = action.payload.datesBounds;
      state.contract = action.payload.contract;
      state.contracts = action.payload.contracts;
      state.contractId = action.payload.contractId;
      state.contractWithAvailabilityAccess = action.payload.contractWithAvailabilityAccess;
    },

    getResourceDetails(state, action) {
      state.isLoadingResourceDetails = false;
      const {
        degrees,
        empowerments,
        equipments,
        formalities,
        jobs,
        languages,
        degreeIds,
        empowermentIds,
        equipmentIds,
        formalityIds,
        jobIds,
        languageIds,
        nationalityIds
      } = action.payload;
      state.empowerments = empowerments;
      state.formalities = formalities;
      state.equipments = equipments;
      state.degrees = degrees;
      state.jobs = jobs;
      state.languages = languages;
      state.resource = {
        ...state.resource,
        degreeIds,
        empowermentIds,
        equipmentIds,
        formalityIds,
        jobIds,
        languageIds,
        nationalityIds
      };
    },

    getEditResourceForm(state, action) {
      state.resourceEditForm = action.payload;
      state.isLoadingResourceEditForm = false;
    },

    getResources(state, action) {
      const { props, resources, companies, sectors, jobs, languages, empowerments, formalities, degrees, equipments } =
        action.payload;

      state.getResourcesProps = props;
      state.pickerDate = moment(props.month, 'YYYYMM').toDate();
      state.resources = resources;
      state.filterOptions.companies = companies;
      state.filterOptions.sectors = sectors;
      state.filterOptions.jobs = jobs;
      state.filterOptions.languages = languages;
      state.filterOptions.empowerments = empowerments;
      state.filterOptions.formalities = formalities;
      state.filterOptions.degrees = degrees;
      state.filterOptions.equipments = equipments;
      state.filterOptionsDatas.companies = companies;
      state.filterOptionsDatas.sectors = sectors;
      state.filterOptionsDatas.jobs = jobs;
      state.filterOptionsDatas.languages = languages;
      state.filterOptionsDatas.empowerments = empowerments;
      state.filterOptionsDatas.formalities = formalities;
      state.filterOptionsDatas.degrees = degrees;
      state.filterOptionsDatas.equipments = equipments;
      // suite au CW-2176 => on enlève seulement si pas dans les résultats...
      Object.keys(state.filters).forEach((key) => {
        // pour contractTypes => obliger de le faire dans getContractTypes
        // pour search + counters => on garde les mêmes
        if (
          ![
            'search',
            'counters',
            'contractTypes',
            'external',
            'dateAvailability',
            'datetimeStart',
            'datetimeEnd'
          ].includes(key)
        ) {
          if (state.filters[key] && state.filters[key].length) {
            // on retire les données superflues => on ressort les ids des données reçues en requête
            const dataResult = [...action.payload[key]].map((el) => el.id);
            // on met à jour
            state.filters[key] = state.filters[key].filter((data) => dataResult.includes(data));
          }
        }
      });

      const { resourceId, checked } = proc.getQueryParams(state);
      if (resourceId) {
        const resource = find(resources, (el) => el.id === resourceId);
        if (resource) {
          state.resource = resource;
        }
      } else if (resources[0]) {
        const [resource] = resources;
        state.resource = resource;
        state.resourceId = resource.id;
      }
      if (checked === false) {
        state.filters.counters = '1';
      }
      state.isLoading = false;
    },

    onSelectResource(state, action) {
      state.resourceId = parseInt(action.payload, 10);
      state.resource = find(state.resources, (el) => el.id === state.resourceId);
    },

    onSelectContract(state, action) {
      state.contractId = parseInt(action.payload, 10);
      state.contract = find(state.contracts, (el) => el.id === state.contractId);
    },

    setDateResources(state, action) {
      state.pickerDate = action.payload;
      state.month = moment(action.payload).format('YYYYMM');
    },

    // SORT
    sortByResources(state, action) {
      const idx = state.sortBy.findIndex((el) => el === action.payload);
      if (idx >= 0) {
        if (state.sortOrders[0] === 'desc') {
          state.sortBy.splice(0, 1);
          state.sortOrders.splice(0, 1);
        } else {
          state.sortOrders[0] = 'desc';
        }
      } else {
        state.sortBy[0] = action.payload;
        state.sortOrders[0] = 'asc';
      }
    },

    // FILTER
    filterResources(state, action) {
      state.filters = { ...state.filters, ...action.payload };

      // Si l'utilisateur a sélectionné une date et que le filtre "Seulement les agents disponibles" est actif
      if (action.payload.dateAvailability && state.filters.counters === '2') {
        state.lastUserSelectedDate = action.payload.dateAvailability;
        state.lastUserSelectedTimeStart = action.payload.datetimeStart;
        state.lastUserSelectedTimeEnd = action.payload.datetimeEnd;
      }
    },

    setFilterToAvoid(state, action) {
      state.keyToAvoid = action.payload;
    },

    updateFilters(state, action) {
      state.filterOptions = action.payload;
    },

    // RESET FILTER
    resetFilterResources(state, action) {
      // Réinitialiser les filtres
      state.filters = createDefaultFilters(action.payload?.external);

      // S'assurer que les dates sont explicitement nulles
      state.filters.dateAvailability = null;
      state.filters.datetimeStart = null;
      state.filters.datetimeEnd = null;

      // S'assurer que le champ de recherche est explicitement vide
      state.filters.search = '';

      // Réinitialiser le mois au mois actuel
      const currentDate = new Date();
      state.pickerDate = currentDate;
      state.month = moment(currentDate).format('YYYYMM');

      // Réinitialiser les dates sélectionnées par l'utilisateur
      state.lastUserSelectedDate = null;
      state.lastUserSelectedTimeStart = null;
      state.lastUserSelectedTimeEnd = null;
    },

    resetFilterCounters(state) {
      state.filters.counters = 'all';
      state.filters.dateAvailability = null;
      state.filters.datetimeStart = null;
      state.filters.datetimeEnd = null;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const {
  filterResources,
  resetFilterCounters,
  resetFilterResources,
  resetResource,
  setDateResources,
  setFilterToAvoid,
  setRemoveDemand,
  sortByResources,
  updateFilters,
  onSelectResource,
  onSelectContract
} = slice.actions;

export function getCounters({ contractId, month }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading({ loadingKey: 'isLoadingCounters' }));
    try {
      const result = await socket.get('/counters', {
        contractId,
        month
      });
      dispatch(slice.actions.getCounters(result));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getContractTypes({ month }) {
  return async (dispatch) => {
    try {
      const result = await socket.get('/contract/types', { month });
      dispatch(slice.actions.getContractTypes(result));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getResources(props, reload = false) {
  const { month, dateAvailability, datetimeStart, datetimeEnd } = props;

  return async (dispatch, getState) => {
    const { getResourcesProps } = getState().resources;

    if (reload || !isEqual(props, getResourcesProps)) {
      dispatch(slice.actions.startLoading({ loadingKey: 'isLoading' }));
    }

    try {
      const result = await socket.get('/resources', {
        month,
        dateAvailability,
        datetimeStart,
        datetimeEnd
      });

      dispatch(slice.actions.getResources({ ...result, props }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getResource({ resourceId } = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading({ loadingKey: 'isLoading' }));

    try {
      dispatch(slice.actions.startLoading({ loadingKey: 'isLoadingResource' }));
      const { resources, hasRights } = await socket.get('/resources', { resourceId });
      dispatch(
        slice.actions.getResource({
          resource: resources,
          hasRights: hasRights === 1
        })
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.stopLoading({ loadingKey: 'isLoadingResource' }));
      dispatch(slice.actions.stopLoading({ loadingKey: 'isLoading' }));
    }
  };
}

export function getResourceContracts({ resourceId, month }) {
  return async (dispatch) => {
    const { contractId: urlContractId } = proc.getQueryParams();

    try {
      dispatch(slice.actions.startLoading({ loadingKey: 'isLoadingContracts' }));
      const { contracts, datesBounds } = await socket.get('/contracts', { resourceId, month });

      let contractWithAvailabilityAccess = [];
      if (contracts[0]) {
        contractWithAvailabilityAccess = contracts.filter((el) => el.can_access_availabilities);
      }

      // Sélection du contrat à utiliser
      let updatedContract;

      // On prend le contrat de l'URL s'il existe, sinon le premier contrat
      if (urlContractId && contracts.length > 0) {
        updatedContract = contracts.find((el) => el.id === parseInt(urlContractId, 10));
      }

      if (!updatedContract && contracts.length > 0) {
        [updatedContract] = contracts;
      } else if (!updatedContract) {
        updatedContract = { id: null };
      }

      dispatch(
        slice.actions.getResourceContracts({
          contractId: updatedContract.id,
          contract: updatedContract,
          contracts: contracts.length > 0 ? contracts : [],
          datesBounds,
          contractWithAvailabilityAccess
        })
      );

      dispatch(slice.actions.stopLoading({ loadingKey: 'isLoadingContracts' }));
    } catch (err) {
      dispatch(slice.actions.hasError(err));
    }
  };
}

export function getResourceDetails({ resourceId }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading({ loadingKey: 'isLoadingResourceDetails' }));
    try {
      const result = await socket.get('/resource/details', { resourceId });
      dispatch(slice.actions.getResourceDetails(result));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getEditResourceForm() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading({ loadingKey: 'isLoadingResourceEditForm' }));
    try {
      const result = await socket.get('/demands/partner/form');
      dispatch(slice.actions.getEditResourceForm(result));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
